import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import {
  getCustomerPortalWorkorders,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

const VIEW_STORAGE_KEY = "portal.workorders.view";
const DEFAULT_LIMIT = 25;

export default function PortalWorkordersPage() {
  const { user } = useUser();
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState(() => loadViewMode());
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = DEFAULT_LIMIT;

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (isCustomerPortalUser) {
          const res = await getCustomerPortalWorkorders({
            page,
            limit,
            q: String(query || "").trim() || undefined,
          });
          if (!active) return;
          setItems(Array.isArray(res?.items) ? res.items : []);
          setTotal(Number(res?.total || 0));
          return;
        }

        const workorders = await loadInternalWorkorders();
        if (!active) return;
        const source = workorders.filter(isWorkorderVisibleForClientHub);
        const q = String(query || "").trim().toLowerCase();
        const filtered = !q
          ? source
          : source.filter((item) => {
            const text = [
              item?.workorderNumber,
              item?.werkbonNummer,
              item?.title,
              item?.omschrijving,
              item?.status,
              item?.projectNumber,
              item?.projectNummer,
            ]
              .map((value) => String(value || "").toLowerCase())
              .join(" ");
            return text.includes(q);
          });
        const nextTotal = filtered.length;
        const nextTotalPages = Math.max(1, Math.ceil(nextTotal / limit));
        const safePage = Math.min(Math.max(1, page), nextTotalPages);
        const start = (safePage - 1) * limit;
        setItems(filtered.slice(start, start + limit));
        setTotal(nextTotal);
        if (safePage !== page) {
          setPage(safePage);
        }
      } catch (err) {
        if (!active) return;
        setError(
          isCustomerPortalUser
            ? toPortalErrorMessage(err, "Werkbonnen laden mislukt")
            : err?.response?.data?.message || "Werkbonnen laden mislukt"
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isCustomerPortalUser, limit, page, query]);

  useEffect(() => {
    saveViewMode(view);
  }, [view]);

  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    return {
      page: safePage,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [limit, page, total]);

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Werkbonnen</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">Werkbonnen</h1>
          <p className="text-sm text-slate-600">Overzicht van werkbonnen in dit portaal.</p>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Zoek op nummer, omschrijving of status"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-80"
            />

            <div className="inline-flex rounded-xl border border-slate-300 bg-white p-1">
              <button
                type="button"
                onClick={() => setView("table")}
                className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                  view === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Tabel
              </button>
              <button
                type="button"
                onClick={() => setView("cards")}
                className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                  view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Kaarten
              </button>
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between gap-2 px-2 text-xs text-slate-600">
            <p>
              Pagina <span className="font-semibold text-slate-800">{pagination.page}</span> van{" "}
              <span className="font-semibold text-slate-800">{pagination.totalPages}</span> • Totaal:{" "}
              <span className="font-semibold text-slate-800">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={!pagination.hasPrevious || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(pagination.totalPages, current + 1))
                }
                disabled={!pagination.hasNext || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <p className="px-2 py-4 text-sm text-slate-500">Laden...</p>
          ) : items.length === 0 ? (
            <p className="px-2 py-4 text-sm text-slate-500">Geen werkbonnen gevonden.</p>
          ) : view === "table" ? (
            <div className="space-y-2">
              <div className="grid gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid-cols-[1fr_160px_170px_120px]">
                <span>Werkbon</span>
                <span>Project</span>
                <span>Datum</span>
                <span>Status</span>
              </div>

              {items.map((item) => (
                <Link
                  key={String(item?.id || item?._id || "")}
                  to={`/portaal/werkbonnen/${item?.id || item?._id}`}
                  className="grid gap-2 rounded-xl border border-slate-200 p-3 hover:border-blue-300 hover:bg-blue-50 md:grid-cols-[1fr_160px_170px_120px]"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item?.workorderNumber || item?.werkbonNummer || "-"}</p>
                    <p className="text-xs text-slate-600">{item?.title || item?.omschrijving || "Geen omschrijving"}</p>
                  </div>
                  <p className="text-sm text-slate-700">{item?.projectNumber || item?.projectNummer || "-"}</p>
                  <p className="text-sm text-slate-700">{formatDate(item?.plannedDate || item?.createdAt)}</p>
                  <StatusBadge status={item?.status}>{item?.statusLabel || formatStatus(item?.status)}</StatusBadge>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((item) => (
                <Link
                  key={String(item?.id || item?._id || "")}
                  to={`/portaal/werkbonnen/${item?.id || item?._id}`}
                  className="rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:bg-blue-50"
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item?.workorderNumber || item?.werkbonNummer || "-"}</p>
                      <p className="text-xs text-slate-600">{item?.title || item?.omschrijving || "Geen omschrijving"}</p>
                    </div>
                    <StatusBadge status={item?.status}>{item?.statusLabel || formatStatus(item?.status)}</StatusBadge>
                  </div>
                  <div className="grid gap-1 text-xs text-slate-600">
                    <p>Project: {item?.projectNumber || item?.projectNummer || "-"}</p>
                    <p>Datum: {formatDate(item?.plannedDate || item?.createdAt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function StatusBadge({ status, children }) {
  const key = String(status || "").trim().toLowerCase();
  const className =
    key === "afgerond" || key === "gesloten"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : key === "wacht_op_goedkeuring" || key === "wacht_op_materiaal"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-blue-200 bg-blue-50 text-blue-700";

  return (
    <span className={`inline-flex h-fit w-fit rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function formatStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "-";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
  }).format(date);
}

function isWorkorderVisibleForClientHub(item) {
  return Boolean(
    item?.workorderTypeFlow?.publishToClientHub ||
      item?.completionFlow?.publishToClientHub ||
      item?.publishToClientHub
  );
}

async function loadInternalWorkorders() {
  try {
    const res = await api.get("/werkbonnen");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    const fallbackRes = await api.get("/werkbonnen/mine");
    return Array.isArray(fallbackRes?.data) ? fallbackRes.data : [];
  }
}

function loadViewMode() {
  try {
    const stored = String(window.localStorage.getItem(VIEW_STORAGE_KEY) || "").trim();
    return stored === "cards" ? "cards" : "table";
  } catch {
    return "table";
  }
}

function saveViewMode(view) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view === "cards" ? "cards" : "table");
  } catch {
    // ignore localStorage failure
  }
}
